// src/components/verified/VerifiedBadge.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../lib/firebase';
import { supabase } from '../../lib/supabaseClient';
import {
  Shield, ShieldCheck, Star, Gem, X, Check, AlertTriangle, Info,
  Loader, Upload, Camera, FileText, CreditCard, CheckCircle,
  XCircle, Clock, ChevronRight, BadgeCheck, Lock, RefreshCw
} from 'lucide-react';
import './VerifiedBadge.css';

/* ============================================================
   TYPES
   ============================================================ */
export type PlanType = 'silver' | 'gold' | 'diamond';
export type StatusType = 'in_progress' | 'pending_review' | 'success' | 'rejected' | 'expired';

export interface VerificationRow {
  id: string;
  user_id: string;
  shop_id: string;
  plan: PlanType;
  status: StatusType;
  nin_image_url: string | null;
  proof_of_address_url: string | null;
  cac_image_url: string | null;
  admin_note: string | null;
  fee_paid: number;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

interface VerifiedBadgeProps {
  /** vendor_profiles.vendor_id — REQUIRED */
  shopId: string;
  variant?: 'button' | 'badge' | 'both';
  className?: string;
  onStatusChange?: (row: VerificationRow | null) => void;
}

/* ============================================================
   PLAN CONFIG
   ============================================================ */
const PLANS: Record<PlanType, {
  id: PlanType; name: string; icon: React.ReactNode;
  color: string; bgColor: string; borderColor: string; badgeColor: string;
  fee: number; requirements: string[]; description: string;
}> = {
  silver: {
    id: 'silver', name: 'Silver', icon: <Shield size={18} />,
    color: '#475569', bgColor: '#f8fafc', borderColor: '#cbd5e1', badgeColor: '#94a3b8',
    fee: 3000,
    requirements: ['NIN (National ID Number)'],
    description: 'Regular verification badge'
  },
  gold: {
    id: 'gold', name: 'Gold', icon: <Star size={18} />,
    color: '#b45309', bgColor: '#fffbeb', borderColor: '#fcd34d', badgeColor: '#f59e0b',
    fee: 25000,
    requirements: ['NIN (National ID Number)', 'Proof of Address'],
    description: 'Recommended — higher trust & visibility'
  },
  diamond: {
    id: 'diamond', name: 'Diamond', icon: <Gem size={18} />,
    color: '#1d4ed8', bgColor: '#eff6ff', borderColor: '#93c5fd', badgeColor: '#3b82f6',
    fee: 95000,
    requirements: ['NIN (National ID Number)', 'Proof of Address', 'CAC Registration'],
    description: 'Limited slots — maximum trust & features'
  }
};

/* ============================================================
   HELPERS
   ============================================================ */
const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(n);

export const getBadgeIcon = (plan: PlanType, size: number = 16): React.ReactNode => {
  const c = PLANS[plan].badgeColor;
  switch (plan) {
    case 'silver':  return <ShieldCheck size={size} fill={c} color={c} />;
    case 'gold':    return <BadgeCheck  size={size} fill={c} color={c} />;
    case 'diamond': return <Gem         size={size} fill={c} color={c} />;
  }
};

/* ============================================================
   BADGE VISUAL (used inline + inside plan preview modal)
   - Silver  : ShieldCheck Verified  → bright silver text, dark background
   - Gold    : 5 × Star              → transparent bg, NO border
   - Diamond : Gem Recommended       → keeps current bg
   ============================================================ */
const BadgeVisual: React.FC<{
  plan: PlanType;
  size?: 'sm' | 'md';
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
  style?: React.CSSProperties;
}> = ({ plan, size = 'sm', className = '', onClick, title, style }) => {
  const baseStyle = size === 'sm'
    ? { fontSize: 10, padding: '2px 7px', gap: 3 }
    : { fontSize: 11, padding: '4px 10px', gap: 4 };

  const clickable = !!onClick;

  if (plan === 'silver') {
    return (
      <div
        className={`vb-display vb-display-silver ${className}`}
        onClick={onClick}
        title={title}
        style={{
          ...baseStyle,
          background: 'linear-gradient(135deg,#1f2937,#0f172a)',
          border: '1px solid #cbd5e1',
          color: '#e2e8f0',
          cursor: clickable ? 'pointer' : 'default',
          ...style
        }}
      >
        <ShieldCheck
          size={size === 'sm' ? 15 : 20}
          color="#e2e8f0"
          fill="#e2e8f0"
          style={{ filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.5))' }}
        />
        <span className="vb-display-label" style={{ color: '#e2e8f0', fontWeight: 700 }}>
          Verified
        </span>
      </div>
    );
  }

  if (plan === 'gold') {
    return (
      <div
        className={`vb-display vb-display-gold ${className}`}
        onClick={onClick}
        title={title}
        style={{
          ...baseStyle,
          background: 'transparent',
          border: 'none',
          color: '#f59e0b',
          cursor: clickable ? 'pointer' : 'default',
          ...style
        }}
      >
        {[0, 1, 2, 3, 4].map(i => (
          <Star
            key={i}
            size={size === 'sm' ? 15 : 20}
            fill="#f59e0b"
            color="#f59e0b"
          />
        ))}
      </div>
    );
  }

  // diamond
  return (
    <div
      className={`vb-display vb-display-diamond ${className}`}
      onClick={onClick}
      title={title}
      style={{
        ...baseStyle,
        background: '#eff6ff',
        border: '1px solid #93c5fd',
        color: '#1d4ed8',
        cursor: clickable ? 'pointer' : 'default',
        ...style
      }}
    >
      <Gem size={size === 'sm' ? 15 : 20} color="#3b82f6" fill="#3b82f6" />
      <span className="vb-display-label" style={{ color: '#1d4ed8', fontWeight: 700 }}>
        Recommended
      </span>
    </div>
  );
};

/* ============================================================
   IMAGE → COMPRESSED BASE64 DATA URL
   ============================================================ */
const fileToCompressedDataUrl = (file: File, maxDim = 1000, quality = 0.72): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please upload an image file.'));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      reject(new Error('Image must be under 8MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Invalid image file.'));
      img.onload = () => {
        try {
          let { width, height } = img;

          if (width > maxDim || height > maxDim) {
            if (width >= height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas unsupported.')); return; }

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl);
        } catch (e) {
          reject(e);
        }
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });

/* ============================================================
   PORTAL
   ============================================================ */
const VerifiedPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted || typeof document === 'undefined') return null;
  return createPortal(children, document.body);
};

/* ============================================================
   COMPONENT
   ============================================================ */
const VerifiedBadge: React.FC<VerifiedBadgeProps> = ({
  shopId, variant = 'both', className = '', onStatusChange
}) => {
  const navigate = useNavigate();

  const [row, setRow]                 = useState<VerificationRow | null>(null);
  const [loading, setLoading]         = useState(true);
  const [balance, setBalance]         = useState(0);
  const [isOwner, setIsOwner]         = useState(false);

  const [showPlan, setShowPlan]       = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [showStatus, setShowStatus]   = useState(false);
  const [plan, setPlan]               = useState<PlanType>('gold');

  const [files, setFiles]             = useState<{
    nin: File | null; proof: File | null; cac: File | null;
  }>({ nin: null, proof: null, cac: null });

  const [previews, setPreviews]       = useState({
    nin: '', proof: '', cac: ''
  });

  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [toast, setToast]             = useState<string | null>(null);

  const ninRef   = useRef<HTMLInputElement>(null);
  const proofRef = useRef<HTMLInputElement>(null);
  const cacRef   = useRef<HTMLInputElement>(null);

  /* ========= FETCH VERIFICATION ROW ========= */
  const fetchRow = useCallback(async () => {
    if (!shopId) { setLoading(false); return; }
    try {
      const { data, error: e } = await supabase
        .from('verified_shops')
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();

      if (e) console.error('Fetch verification error:', e);
      const r = (data as VerificationRow) || null;
      setRow(r);
      onStatusChange?.(r);
    } catch (err) {
      console.error('fetchRow exception:', err);
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [shopId, onStatusChange]);

  /* ========= FETCH BALANCE + OWNERSHIP ========= */
  const fetchBalance = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) { setBalance(0); setIsOwner(false); return; }
    try {
      const { data } = await supabase
        .from('users')
        .select('balance')
        .eq('firebase_uid', u.uid)
        .maybeSingle();
      setBalance(Number(data?.balance) || 0);
    } catch { setBalance(0); }
  }, []);

  const checkOwnership = useCallback(async () => {
    const u = auth.currentUser;
    if (!u || !shopId) { setIsOwner(false); return; }
    try {
      const { data } = await supabase
        .from('vendor_profiles')
        .select('user_id')
        .eq('vendor_id', shopId)
        .maybeSingle();
      setIsOwner(!!data && data.user_id === u.uid);
    } catch {
      setIsOwner(false);
    }
  }, [shopId]);

  useEffect(() => {
    fetchRow();
    fetchBalance();
    checkOwnership();
  }, [fetchRow, fetchBalance, checkOwnership]);

  /* ========= OPEN FORM ========= */
  const openForm = (p: PlanType) => {
    setPlan(p);
    setError(null);
    setFiles({ nin: null, proof: null, cac: null });
    setPreviews({ nin: '', proof: '', cac: '' });
    setShowPlan(false);
    setShowForm(true);
  };

  /* ========= FILE PICK ========= */
  const pick = (
    e: React.ChangeEvent<HTMLInputElement>,
    k: 'nin' | 'proof' | 'cac'
  ) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setError('Please upload an image'); return; }
    if (f.size > 8 * 1024 * 1024) { setError('Image must be under 8MB'); return; }

    setFiles(prev => ({ ...prev, [k]: f }));
    setPreviews(prev => ({ ...prev, [k]: URL.createObjectURL(f) }));
    setError(null);
  };

  /* ========= FORM VALIDATION ========= */
  const needProof = plan === 'gold' || plan === 'diamond';
  const needCac   = plan === 'diamond';
  const fee       = PLANS[plan].fee;
  const enough    = balance >= fee;

  const formValid = useMemo(() => (
    !!files.nin &&
    (!needProof || !!files.proof) &&
    (!needCac   || !!files.cac) &&
    enough
  ), [files, needProof, needCac, enough]);

  const disabledReason = (): string => {
    if (!files.nin) return 'Upload your NIN / ID card';
    if (needProof && !files.proof) return 'Upload proof of address';
    if (needCac   && !files.cac)   return 'Upload CAC document';
    if (!enough) return `Insufficient balance — need ${formatCurrency(fee - balance)} more`;
    return '';
  };

  /* ========= SUBMIT ========= */
  const submit = async () => {
    const u = auth.currentUser;
    if (!u)              { setError('You must be signed in'); return; }
    if (!shopId)         { setError('Missing shop id'); return; }
    if (!formValid)      { setError(disabledReason()); return; }

    setSubmitting(true);
    setError(null);

    try {
      const ninData = await fileToCompressedDataUrl(files.nin!);

      let proofData: string | null = null;
      if (files.proof) proofData = await fileToCompressedDataUrl(files.proof);

      let cacData: string | null = null;
      if (files.cac) cacData = await fileToCompressedDataUrl(files.cac);

      const { data: uData, error: uErr } = await supabase
        .from('users')
        .select('balance')
        .eq('firebase_uid', u.uid)
        .single();

      if (uErr) throw uErr;

      const before = Number(uData?.balance) || 0;
      if (before < fee) throw new Error('Insufficient balance');

      const after = before - fee;
      const nowIso = new Date().toISOString();

      const { error: balErr } = await supabase
        .from('users')
        .update({ balance: after, updated_at: nowIso })
        .eq('firebase_uid', u.uid);

      if (balErr) throw balErr;
      setBalance(after);

      const payload = {
        user_id: u.uid,
        shop_id: shopId,
        plan,
        status: 'in_progress' as StatusType,
        nin_image_url: ninData,
        proof_of_address_url: proofData,
        cac_image_url: cacData,
        fee_paid: fee,
        updated_at: nowIso
      };

      const { data: existing } = await supabase
        .from('verified_shops')
        .select('id')
        .eq('shop_id', shopId)
        .maybeSingle();

      if (existing?.id) {
        const { error: upErr } = await supabase
          .from('verified_shops')
          .update(payload)
          .eq('shop_id', shopId);
        if (upErr) throw upErr;
      } else {
        const { error: insErr } = await supabase
          .from('verified_shops')
          .insert([{ ...payload, created_at: nowIso }]);
        if (insErr) throw insErr;
      }

      try {
        await supabase.from('verification_transactions').insert([{
          user_id: u.uid,
          shop_id: shopId,
          plan,
          amount: fee,
          balance_before: before,
          balance_after: after,
          reference: `verify_${plan}_${Date.now()}`,
          created_at: nowIso
        }]);
      } catch { /* ignore */ }

      setToast('Verification submitted! Review usually takes 24–48h.');
      setTimeout(() => setToast(null), 4000);

      setShowForm(false);
      setFiles({ nin: null, proof: null, cac: null });
      setPreviews({ nin: '', proof: '', cac: '' });
      await fetchRow();
    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     UPGRADE / RETRY — opens plan modal, but only if this shop
     belongs to the logged-in user. Otherwise, redirect to
     vendor dashboard (where they can create/access a shop).
     ============================================================ */
  const handleUpgrade = async () => {
    const u = auth.currentUser;
    if (!u) {
      navigate('/signin');
      return;
    }

    // Re-verify ownership live (in case state is stale)
    let owns = isOwner;
    try {
      const { data } = await supabase
        .from('vendor_profiles')
        .select('user_id')
        .eq('vendor_id', shopId)
        .maybeSingle();
      owns = !!data && data.user_id === u.uid;
      setIsOwner(owns);
    } catch {
      owns = isOwner;
    }

    if (!owns) {
      // Not this user's shop — bail to vendor dashboard
      setShowStatus(false);
      navigate('/vendor/dashboard');
      return;
    }

    setShowStatus(false);
    setShowPlan(true);
  };

  /* ============================================================
     RENDER HELPERS
     ============================================================ */
  const renderButton = () => {
    if (!shopId) return null;

    if (loading) {
      return (
        <button className={`vb-btn vb-loading ${className}`} disabled>
          <Loader size={14} className="vb-spin" />
          <span>Loading…</span>
        </button>
      );
    }

    if (row?.status === 'success') {
      return (
        <button
          className={`vb-btn vb-verified vb-verified-${row.plan} ${className}`}
          onClick={() => setShowStatus(true)}
          title="View verification status"
        >
          {getBadgeIcon(row.plan, 14)}
          <span>{PLANS[row.plan].name} Verified</span>
          <Info size={11} />
        </button>
      );
    }

    if (row && (row.status === 'in_progress' || row.status === 'pending_review')) {
      return (
        <button
          className={`vb-btn vb-pending ${className}`}
          onClick={() => setShowStatus(true)}
        >
          <Clock size={14} />
          <span>Verification Pending</span>
          <Info size={11} />
        </button>
      );
    }

    if (row && (row.status === 'rejected' || row.status === 'expired')) {
      return (
        <button
          className={`vb-btn vb-retry ${className}`}
          onClick={() => setShowPlan(true)}
        >
          <RefreshCw size={14} />
          <span>{row.status === 'rejected' ? 'Retry Verification' : 'Renew Badge'}</span>
        </button>
      );
    }

    return (
      <button
        className={`vb-btn vb-get ${className}`}
        onClick={() => setShowPlan(true)}
      >
        <ShieldCheck size={14} />
        <span>Get Verified</span>
      </button>
    );
  };

  const renderBadge = () => {
    if (!row || row.status !== 'success') return null;
    const p = PLANS[row.plan];

    // Clickable ONLY for the shop owner. Silent no-op for everyone else.
    const handleClick = isOwner
      ? (e: React.MouseEvent) => { e.stopPropagation(); setShowStatus(true); }
      : undefined;

    return (
      <BadgeVisual
        plan={row.plan}
        size="sm"
        className={className}
        onClick={handleClick}
        title={isOwner ? `${p.name} Verified — tap to manage` : `${p.name} Verified`}
      />
    );
  };

  /* ============================================================
     JSX
     ============================================================ */
  return (
    <>
      {variant === 'button' && renderButton()}
      {variant === 'badge'  && renderBadge()}
      {variant === 'both'   && renderButton()}

      {/* ============ PLAN SELECTION ============ */}
      {showPlan && (
        <VerifiedPortal>
          <div className="vb-overlay" onClick={() => setShowPlan(false)}>
            <div className="vb-modal vb-plan-modal" onClick={e => e.stopPropagation()}>
              <div className="vb-head">
                <div className="vb-head-title">
                  <ShieldCheck size={20} color="#10b981" />
                  <h2>Get Verified</h2>
                </div>
                <button className="vb-close" onClick={() => setShowPlan(false)}>
                  <X size={18} />
                </button>
              </div>

              <div className="vb-body">
                <div className="vb-intro">
                  <p>
                    <strong>Why get verified?</strong> Increase buyer trust, unlock sponsored
                    placements, and appear higher in search results.
                  </p>
                </div>

                <div className="vb-plans">
                  {(Object.keys(PLANS) as PlanType[]).map(k => {
                    const p = PLANS[k];
                    const isCurrent = row?.plan === k && row?.status === 'success';
                    const isRec = k === 'gold';
                    return (
                      <div
                        key={k}
                        className={`vb-plan ${isRec ? 'vb-plan-rec' : ''} ${isCurrent ? 'vb-plan-cur' : ''}`}
                        style={{ borderColor: p.borderColor, background: p.bgColor }}
                        onClick={() => openForm(k)}
                      >
                        {isRec && <div className="vb-ribbon">Recommended</div>}
                        {isCurrent && (
                          <div className="vb-cur-tag"><Check size={10} /> Current</div>
                        )}

                        <div className="vb-plan-icon" style={{ color: p.color }}>{p.icon}</div>
                        <h3 style={{ color: p.color }}>{p.name} Badge</h3>
                        <div className="vb-plan-fee" style={{ color: p.color }}>
                          {formatCurrency(p.fee)}
                        </div>
                        <p className="vb-plan-desc">{p.description}</p>

                        {/* Live badge preview so users see how their badge will look */}
                        <div className="vb-plan-preview">
                          <span className="vb-plan-preview-label">Preview</span>
                          <BadgeVisual plan={k} size="md" />
                        </div>

                        <ul className="vb-plan-req">
                          {p.requirements.map((r, i) => (
                            <li key={i}><Check size={10} /> {r}</li>
                          ))}
                        </ul>

                        <button
                          className="vb-plan-btn"
                          style={{ background: p.color }}
                          onClick={e => { e.stopPropagation(); openForm(k); }}
                        >
                          {isCurrent ? 'Renew' : 'Select'} {p.name}
                          <ChevronRight size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="vb-plan-note">
                  <Info size={12} />
                  <span>
                    You can upgrade or renew later. Ensure details are correct to speed up
                    verification. <strong>Contact support for more info.</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </VerifiedPortal>
      )}

      {/* ============ FORM ============ */}
      {showForm && (
        <VerifiedPortal>
          <div className="vb-overlay" onClick={() => !submitting && setShowForm(false)}>
            <div className="vb-modal vb-form-modal" onClick={e => e.stopPropagation()}>
              <div className="vb-head">
                <div className="vb-head-title">
                  {PLANS[plan].icon}
                  <h2>{PLANS[plan].name} Verification</h2>
                </div>
                <button
                  className="vb-close"
                  onClick={() => !submitting && setShowForm(false)}
                  disabled={submitting}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="vb-body">
                {error && (
                  <div className="vb-alert vb-alert-err">
                    <AlertTriangle size={14} /> <span>{error}</span>
                  </div>
                )}

                <div className="vb-balance-row">
                  <CreditCard size={14} />
                  <span>Balance:</span><strong>{formatCurrency(balance)}</strong>
                  <span className="vb-sep">•</span>
                  <span>Fee:</span>
                  <strong style={{ color: PLANS[plan].color }}>{formatCurrency(fee)}</strong>
                </div>

                {/* NIN */}
                <div className="vb-upload">
                  <label className="vb-upload-label">
                    <FileText size={14} /> NIN / ID Card <span className="vb-req">*</span>
                  </label>
                  <p className="vb-upload-hint">Clear photo of NIN slip or national ID</p>
                  <div
                    className={`vb-upload-box ${previews.nin ? 'vb-has' : ''}`}
                    onClick={() => ninRef.current?.click()}
                  >
                    {previews.nin ? (
                      <div className="vb-preview">
                        <img src={previews.nin} alt="NIN" />
                        <div className="vb-preview-overlay"><Camera size={16} /><span>Change</span></div>
                      </div>
                    ) : (
                      <div className="vb-placeholder">
                        <Upload size={24} /><span>Click to upload</span>
                        <small>PNG, JPG up to 8MB</small>
                      </div>
                    )}
                    <input
                      ref={ninRef} type="file" accept="image/*" hidden
                      onChange={e => pick(e, 'nin')}
                    />
                  </div>
                </div>

                {/* Proof */}
                {needProof && (
                  <div className="vb-upload">
                    <label className="vb-upload-label">
                      <FileText size={14} /> Proof of Address <span className="vb-req">*</span>
                    </label>
                    <p className="vb-upload-hint">Utility bill (NEPA, water, etc.)</p>
                    <div
                      className={`vb-upload-box ${previews.proof ? 'vb-has' : ''}`}
                      onClick={() => proofRef.current?.click()}
                    >
                      {previews.proof ? (
                        <div className="vb-preview">
                          <img src={previews.proof} alt="Proof" />
                          <div className="vb-preview-overlay"><Camera size={16} /><span>Change</span></div>
                        </div>
                      ) : (
                        <div className="vb-placeholder">
                          <Upload size={24} /><span>Click to upload</span>
                          <small>PNG, JPG up to 8MB</small>
                        </div>
                      )}
                      <input
                        ref={proofRef} type="file" accept="image/*" hidden
                        onChange={e => pick(e, 'proof')}
                      />
                    </div>
                  </div>
                )}

                {/* CAC */}
                {needCac && (
                  <div className="vb-upload">
                    <label className="vb-upload-label">
                      <FileText size={14} /> CAC Registration <span className="vb-req">*</span>
                    </label>
                    <p className="vb-upload-hint">CAC certificate or registration</p>
                    <div
                      className={`vb-upload-box ${previews.cac ? 'vb-has' : ''}`}
                      onClick={() => cacRef.current?.click()}
                    >
                      {previews.cac ? (
                        <div className="vb-preview">
                          <img src={previews.cac} alt="CAC" />
                          <div className="vb-preview-overlay"><Camera size={16} /><span>Change</span></div>
                        </div>
                      ) : (
                        <div className="vb-placeholder">
                          <Upload size={24} /><span>Click to upload</span>
                          <small>PNG, JPG up to 8MB</small>
                        </div>
                      )}
                      <input
                        ref={cacRef} type="file" accept="image/*" hidden
                        onChange={e => pick(e, 'cac')}
                      />
                    </div>
                  </div>
                )}

                {!formValid && (
                  <div className="vb-locked">
                    <Lock size={12} /> <span>{disabledReason()}</span>
                  </div>
                )}
              </div>

              <div className="vb-foot">
                <button
                  className="vb-btn-cancel"
                  onClick={() => !submitting && setShowForm(false)}
                  disabled={submitting}
                >Cancel</button>

                <button
                  className={`vb-btn-submit ${!formValid ? 'vb-disabled' : ''}`}
                  style={{ background: formValid ? PLANS[plan].color : '#cbd5e1' }}
                  disabled={!formValid || submitting}
                  onClick={submit}
                >
                  {submitting ? (
                    <><Loader size={14} className="vb-spin" /> Processing…</>
                  ) : !enough ? (
                    <><Lock size={14} /> Insufficient Balance</>
                  ) : (
                    <><ShieldCheck size={14} /> Verify Now — {formatCurrency(fee)}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </VerifiedPortal>
      )}

      {/* ============ STATUS ============ */}
      {showStatus && row && (
        <VerifiedPortal>
          <div className="vb-overlay" onClick={() => setShowStatus(false)}>
            <div className="vb-modal vb-status-modal" onClick={e => e.stopPropagation()}>
              <div className="vb-head">
                <div className="vb-head-title">
                  {row.status === 'success'
                    ? getBadgeIcon(row.plan, 20)
                    : row.status === 'in_progress' || row.status === 'pending_review'
                    ? <Clock size={20} color="#f59e0b" />
                    : <XCircle size={20} color="#ef4444" />}
                  <h2>Verification Status</h2>
                </div>
                <button className="vb-close" onClick={() => setShowStatus(false)}>
                  <X size={18} />
                </button>
              </div>

              <div className="vb-body">
                <div className={`vb-status-banner vb-status-${row.status}`}>
                  {row.status === 'success' && (
                    <><CheckCircle size={20} /><div>
                      <strong>Verified!</strong>
                      <p>Your shop has the {PLANS[row.plan].name} badge.</p>
                    </div></>
                  )}
                  {row.status === 'in_progress' && (
                    <><Clock size={20} /><div>
                      <strong>Verification In Progress</strong>
                      <p>Review usually takes 24–48 hours.</p>
                    </div></>
                  )}
                  {row.status === 'pending_review' && (
                    <><Clock size={20} /><div>
                      <strong>Pending Review</strong>
                      <p>Our team is reviewing your documents.</p>
                    </div></>
                  )}
                  {row.status === 'rejected' && (
                    <><XCircle size={20} /><div>
                      <strong>Verification Rejected</strong>
                      <p>See admin note below and try again.</p>
                    </div></>
                  )}
                  {row.status === 'expired' && (
                    <><AlertTriangle size={20} /><div>
                      <strong>Badge Expired</strong>
                      <p>Renew to keep your badge active.</p>
                    </div></>
                  )}
                </div>

                <div className="vb-status-grid">
                  <div className="vb-status-row">
                    <span>Plan</span>
                    <strong style={{ color: PLANS[row.plan].color }}>
                      {PLANS[row.plan].name}
                    </strong>
                  </div>
                  <div className="vb-status-row">
                    <span>Status</span>
                    <strong className={`vb-st-${row.status}`}>
                      {row.status.replace('_', ' ').toUpperCase()}
                    </strong>
                  </div>
                  <div className="vb-status-row">
                    <span>Fee Paid</span>
                    <strong>{formatCurrency(row.fee_paid)}</strong>
                  </div>
                  <div className="vb-status-row">
                    <span>Submitted</span>
                    <strong>{new Date(row.created_at).toLocaleDateString()}</strong>
                  </div>
                  <div className="vb-status-row">
                    <span>Last Updated</span>
                    <strong>{new Date(row.updated_at).toLocaleDateString()}</strong>
                  </div>
                </div>

                {row.admin_note && (
                  <div className="vb-admin-note">
                    <div className="vb-admin-note-head">
                      <Info size={12} /> <strong>Admin Note</strong>
                    </div>
                    <p>{row.admin_note}</p>
                  </div>
                )}
              </div>

              <div className="vb-foot vb-status-foot">
                {/* Upgrade / Retry — only meaningful for the owner.
                    If clicked by a non-owner (e.g. race), it redirects. */}
                <button
                  className="vb-btn-upgrade"
                  onClick={handleUpgrade}
                >
                  <RefreshCw size={14} />
                  Upgrade / Change Plan
                </button>
                <button className="vb-btn-cancel" onClick={() => setShowStatus(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </VerifiedPortal>
      )}

      {toast && (
        <VerifiedPortal>
          <div className="vb-toast">
            <CheckCircle size={14} /> <span>{toast}</span>
          </div>
        </VerifiedPortal>
      )}
    </>
  );
};

export default VerifiedBadge;